import { forwardRef, type TextareaHTMLAttributes, type ForwardedRef } from 'react';

import clsx from 'clsx';
import { Controller, useFormContext } from 'FrontRoyalReactHookForm';
import { Text } from 'Text';

type Props = {
    name: string;
    className?: string;
    wrapperClassName?: string;
    defaultValue?: string;
    resizable?: boolean;
    minRows?: number;
    maxHeight?: 'none' | 32 | 48 | 60 | 80;
    label?: string;
} & TextareaHTMLAttributes<HTMLTextAreaElement>;

const DISABLED_CLASSNAME = 'cursor-not-allowed opacity-70';
const maxHeightClassName = (maxHeight: Props['maxHeight']) =>
    ({
        none: 'overflow-hidden',
        32: 'overflow-y-auto max-h-32',
        48: 'overflow-y-auto max-h-48',
        60: 'overflow-y-auto max-h-60',
        80: 'overflow-y-auto max-h-80',
    })[maxHeight ?? 'none'] ?? '';
/**
 * A textarea that automatically grows its height to match the text content.
 * Adapted from the solution described at https://css-tricks.com/the-cleanest-trick-for-autogrowing-textareas/
 * */

export const AutoGrowTextAreaField = forwardRef(
    (
        {
            className,
            wrapperClassName,
            name,
            defaultValue,
            minRows = 1,
            maxHeight = 'none',
            resizable = false,
            label,
            ...rest
        }: Props,
        ref: ForwardedRef<HTMLTextAreaElement>,
    ) => {
        const { control } = useFormContext();
        return (
            <Controller
                control={control}
                name={name}
                defaultValue={defaultValue}
                render={({ field }) => {
                    const attachRefs = (node: HTMLTextAreaElement | null) => {
                        field.ref(node);
                        if (!ref) {
                            return;
                        }
                        if (typeof ref === 'function') {
                            ref(node);
                        } else {
                            ref.current = node;
                        }
                    };
                    return (
                        <div
                            data-testid="auto-grow-textarea-wrapper"
                            className={clsx(
                                'grid grid-cols-1',
                                label ? '[grid-template-rows:auto_1fr]' : '[grid-template-rows:1fr]',
                                rest.disabled && DISABLED_CLASSNAME,
                                wrapperClassName,
                            )}
                        >
                            {label && (
                                <label
                                    htmlFor={name}
                                    className={clsx('col-start-1 row-start-1', rest.disabled && DISABLED_CLASSNAME)}
                                >
                                    <Text as="div" color="black" className="text-xs font-normal text-black">
                                        {label}
                                    </Text>
                                </label>
                            )}
                            <textarea
                                className={clsx(
                                    'scrollbar-hide',
                                    !resizable && 'resize-none',
                                    label ? '[grid-area:2/1/3/2]' : '[grid-area:1/1/2/2]',
                                    maxHeightClassName(maxHeight),
                                    rest.disabled && DISABLED_CLASSNAME,
                                    className,
                                )}
                                rows={minRows}
                                id={field.name}
                                {...field}
                                {...rest}
                                ref={attachRefs}
                            />
                            <div
                                data-testid="replicated-content"
                                aria-hidden
                                className={clsx(
                                    'scrollbar-hide',
                                    'invisible whitespace-pre-wrap',
                                    label ? '[grid-area:2/1/3/2]' : '[grid-area:1/1/2/2]',
                                    maxHeightClassName(maxHeight),
                                    className,
                                )}
                            >
                                {`${field.value ?? ''}\n`}
                            </div>
                        </div>
                    );
                }}
            />
        );
    },
);
